'use client';

import { motion } from 'framer-motion';
import Link from 'next/link';
import {
  Database, Brain, Zap, Shield, TrendingUp, BarChart3, Globe,
  CheckCircle, ArrowRight, Layers, Server, Eye, Lock, Users,
  Building2, FileText, MessageSquare, Activity, Network
} from 'lucide-react';
import { useTheme } from '@/components/ThemeContext';

/* ═══════════════════════════════════════════════
   Investor Relations — SnapCodex 4대 엔진
   건축 온톨로지 기반 AI 데이터 플랫폼
   ═══════════════════════════════════════════════ */

const SNAPCODEX_ENGINES = [
  {
    name: 'SnapRAG', desc: '통합 RAG 엔진',
    detail: '실시간 건축 전문 답변 — NAS 문서 기반 컨텍스트 생성',
    icon: MessageSquare, color: '#6366f1', status: '운영 중'
  },
  {
    name: 'SnapFoundry', desc: '데이터 통합·변환 엔진',
    detail: 'NAS→Neo4j→Gemini AI 3단계 캐싱 파이프라인',
    icon: Database, color: '#06b6d4', status: '운영 중'
  },
  {
    name: 'SnapAgent', desc: 'AI 에이전트 오케스트레이션',
    detail: 'BOQ·CAD·BTL·설계 4개 에이전트 병렬 크로스 분석',
    icon: Brain, color: '#a855f7', status: '운영 중'
  },
  {
    name: 'SnapOntology', desc: '건축 온톨로지 그래프',
    detail: '프로젝트·문서·FAQ 관계형 그래프 DB (Neo4j)',
    icon: Network, color: '#f59e0b', status: '구축 중 (46%)'
  },
];

const MOAT_METRICS = [
  { label: 'Neo4j 전체 노드', value: '164+', growth: '매일 증가', icon: Database },
  { label: '프로젝트 인덱스', value: '93', target: '202', icon: Building2 },
  { label: '문서 컨텍스트', value: '38', target: '3,000', icon: FileText },
  { label: 'FAQ 캐시', value: '33', target: '540', icon: MessageSquare },
];

const COMPETITIVE_ADVANTAGES = [
  {
    title: '데이터 해자 (Data Moat)',
    desc: 'NAS에 축적된 4,700+개 프로젝트 파일이 Neo4j에 구조화되면, 후발 주자가 동일한 품질의 데이터를 축적하는 데 최소 3~5년 소요',
    icon: Shield, color: '#22c55e'
  },
  {
    title: '도메인 Lock-in',
    desc: '건설/설계 전문 온톨로지 + 업무별 시스템 프롬프트 + 프로젝트별 학습 데이터 — 전환 비용이 시간에 비례해 급증',
    icon: Lock, color: '#f59e0b'
  },
  {
    title: '실증 확보',
    desc: 'A프로젝트 ₩12.85억 규모 실증 프로젝트 완료 — "데이터 투명성으로 설계 변경 누락 0건" 성과',
    icon: Eye, color: '#6366f1'
  },
  {
    title: '팀 경쟁력',
    desc: '설계, 시공, 원가, 관급 경험을 모두 가진 4개사 출신 팀 — 일반 AI 기업이 절대 모방 불가능한 도메인 지식',
    icon: Users, color: '#ef4444'
  },
];

export default function IRPage() {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  const bg = isDark
    ? 'linear-gradient(to bottom, #030712, #0a0f1a, #030712)'
    : 'linear-gradient(to bottom, #f8fafc, #ffffff, #f8fafc)';
  const textPrimary = isDark ? '#fff' : '#0f172a';
  const textSecondary = isDark ? '#9ca3af' : '#64748b';
  const textMuted = isDark ? '#6b7280' : '#94a3b8';
  const textDim = isDark ? '#4b5563' : '#94a3b8';
  const headerBg = isDark ? 'rgba(3,7,18,0.9)' : 'rgba(255,255,255,0.92)';
  const borderLight = isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.06)';
  const borderMedium = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.08)';
  const cardBg = isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)';
  const buttonGhostBg = isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)';
  const buttonGhostBorder = isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)';
  const heroGradText = isDark
    ? 'linear-gradient(to right, #fff, #818cf8, #a855f7)'
    : 'linear-gradient(to right, #0f172a, #4f46e5, #7c3aed)';
  const accentText = isDark ? '#818cf8' : '#4f46e5';

  return (
    <main style={{ minHeight: '100vh', paddingTop: 64, background: bg, color: textPrimary, fontFamily: "'Inter','Pretendard',system-ui,sans-serif" }}>
      {/* Header */}
      <header style={{ borderBottom: `1px solid ${borderLight}`, background: headerBg, backdropFilter: 'blur(20px)', position: 'sticky', top: 0, zIndex: 50 }}>
        <div style={{ maxWidth: 1280, margin: '0 auto', padding: '0 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 64 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ width: 36, height: 36, borderRadius: 12, background: 'linear-gradient(135deg, #6366f1, #a855f7)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <TrendingUp style={{ width: 20, height: 20, color: '#fff' }} />
            </div>
            <div>
              <h1 style={{ fontSize: 14, fontWeight: 800, margin: 0 }}>Investor Relations</h1>
              <p style={{ fontSize: 10, color: textMuted, margin: 0 }}>DesignInsight · 건축 온톨로지 AI 플랫폼</p>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            <a href="/case/ptk" style={{ fontSize: 12, color: accentText, textDecoration: 'none' }}>실증 사례 →</a>
            <a href="/" style={{ fontSize: 12, color: textMuted, textDecoration: 'none' }}>← 홈</a>
          </div>
        </div>
      </header>

      <div style={{ maxWidth: 1280, margin: '0 auto', padding: '48px 24px' }}>
        {/* Hero */}
        <div style={{ textAlign: 'center', marginBottom: 64 }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '8px 20px', borderRadius: 9999, background: 'rgba(99,102,241,0.08)', border: '1px solid rgba(99,102,241,0.2)', marginBottom: 24 }}>
            <Globe style={{ width: 14, height: 14, color: accentText }} />
            <span style={{ fontSize: 12, fontWeight: 700, color: accentText, letterSpacing: 2, textTransform: 'uppercase' }}>건축업계의 온톨로지 선도기업</span>
          </div>
          <h2 style={{ fontSize: 48, fontWeight: 900, lineHeight: 1.2, marginBottom: 16, background: heroGradText, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
            건축 데이터를 투명하게 만듭니다
          </h2>
          <p style={{ fontSize: 18, color: textSecondary, maxWidth: 600, margin: '0 auto', lineHeight: 1.7 }}>
            도면, 내역서, 시방서 — 흩어진 설계 자산을<br />
            <strong style={{ color: textPrimary }}>온톨로지로 구조화하여 AI가 이해하는 지식</strong>으로 전환합니다.
          </p>
        </div>

        {/* SnapCodex 4대 엔진 */}
        <div style={{ marginBottom: 64 }}>
          <h3 style={{ fontSize: 24, fontWeight: 800, marginBottom: 24, display: 'flex', alignItems: 'center', gap: 8 }}>
            <Layers style={{ width: 24, height: 24, color: '#6366f1' }} />
            SnapCodex 4대 엔진
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 16 }}>
            {SNAPCODEX_ENGINES.map((item) => {
              const Icon = item.icon;
              return (
                <div key={item.name} style={{ borderRadius: 16, border: `1px solid ${item.color}30`, background: `${item.color}08`, padding: 24, position: 'relative' }}>
                  <div style={{ position: 'absolute', top: 12, right: 12, fontSize: 10, padding: '2px 10px', borderRadius: 9999, background: item.status.includes('운영') ? 'rgba(16,185,129,0.15)' : 'rgba(245,158,11,0.15)', color: item.status.includes('운영') ? '#10b981' : '#f59e0b', fontWeight: 700 }}>
                    {item.status}
                  </div>
                  <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start' }}>
                    <div style={{ width: 48, height: 48, borderRadius: 12, background: `${item.color}15`, border: `1px solid ${item.color}30`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <Icon style={{ width: 24, height: 24, color: item.color }} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ marginBottom: 4 }}>
                        <span style={{ fontSize: 18, fontWeight: 900, color: item.color }}>{item.name}</span>
                        <span style={{ fontSize: 13, color: textSecondary, marginLeft: 8 }}>{item.desc}</span>
                      </div>
                      <p style={{ fontSize: 12, color: textMuted, margin: '2px 0' }}>{item.detail}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Data Moat 지표 */}
        <div style={{ marginBottom: 64 }}>
          <h3 style={{ fontSize: 24, fontWeight: 800, marginBottom: 24, display: 'flex', alignItems: 'center', gap: 8 }}>
            <Shield style={{ width: 24, height: 24, color: '#22c55e' }} />
            데이터 해자 (Competitive Moat) 지표
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 32 }}>
            {MOAT_METRICS.map((m) => {
              const Icon = m.icon;
              return (
                <div key={m.label} style={{ padding: 24, borderRadius: 16, background: cardBg, border: `1px solid ${borderMedium}` }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                    <Icon style={{ width: 16, height: 16, color: accentText }} />
                    <span style={{ fontSize: 11, fontWeight: 600, color: textSecondary }}>{m.label}</span>
                  </div>
                  <div style={{ fontSize: 36, fontWeight: 900, color: accentText }}>{m.value}</div>
                  {'target' in m && <div style={{ fontSize: 10, color: textDim }}>목표: {m.target}</div>}
                  {'growth' in m && <div style={{ fontSize: 10, color: '#22c55e', fontWeight: 600 }}>{m.growth}</div>}
                </div>
              );
            })}
          </div>

          {/* 경쟁 우위 카드 */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 16 }}>
            {COMPETITIVE_ADVANTAGES.map((adv) => {
              const Icon = adv.icon;
              return (
                <div key={adv.title} style={{ padding: 24, borderRadius: 16, background: `${adv.color}06`, border: `1px solid ${adv.color}20` }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                    <Icon style={{ width: 20, height: 20, color: adv.color }} />
                    <span style={{ fontSize: 15, fontWeight: 800 }}>{adv.title}</span>
                  </div>
                  <p style={{ fontSize: 13, color: textSecondary, lineHeight: 1.6 }}>{adv.desc}</p>
                </div>
              );
            })}
          </div>
        </div>

        {/* 핵심 메시지 */}
        <div style={{ textAlign: 'center', padding: '48px 24px', borderRadius: 24, background: 'linear-gradient(135deg, rgba(99,102,241,0.06), rgba(168,85,247,0.06))', border: '1px solid rgba(99,102,241,0.15)', marginBottom: 64 }}>
          <h3 style={{ fontSize: 28, fontWeight: 900, marginBottom: 16 }}>
            "빠르게 데이터를 축적한 선점 기업이<br />시장을 지배합니다"
          </h3>
          <p style={{ fontSize: 14, color: textSecondary, maxWidth: 600, margin: '0 auto 24px', lineHeight: 1.7 }}>
            4,700+개 프로젝트의 도면·내역서·시방서를 Neo4j 그래프에 온톨로지로 구조화하면,
            후발 주자가 동일한 품질의 <strong style={{ color: accentText }}>도메인 특화 AI 데이터</strong>를 구축하는 데 3~5년이 걸립니다.
          </p>
          <div style={{ display: 'flex', gap: 16, justifyContent: 'center' }}>
            <a href="/case/ptk" style={{ padding: '12px 24px', borderRadius: 12, background: 'linear-gradient(135deg, #6366f1, #a855f7)', color: '#fff', fontSize: 14, fontWeight: 700, textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 8 }}>
              실증 사례 보기 <ArrowRight style={{ width: 16, height: 16 }} />
            </a>
            <a href="/" style={{ padding: '12px 24px', borderRadius: 12, background: buttonGhostBg, border: `1px solid ${buttonGhostBorder}`, color: textSecondary, fontSize: 14, fontWeight: 600, textDecoration: 'none' }}>
              메인으로
            </a>
          </div>
        </div>
      </div>

      <footer style={{ padding: 24, textAlign: 'center', fontSize: 12, color: textDim, borderTop: `1px solid ${borderLight}` }}>
        DesignInsight Inc. — 건축업의 온톨로지 선도기업 © 2026
      </footer>
    </main>
  );
}
